<script lang="ts">
  import { t } from '$core/i18n';
  import {
    listActiveQuarantine,
    requeueFromQuarantine,
    requeueManyFromQuarantine,
    markQuarantineDiscarded,
    markManyQuarantineDiscarded,
    getQuarantineStats,
    type QuarantineStats
  } from '$db/local/repositories/quarantine.repo';
  import { syncStore } from '$features/sync';
  import type { QuarantineRecord } from '$db/local/db';
  import { Button } from '$ui/primitives/Button';
  import { EmptyState } from '$ui/primitives/EmptyState';

  let records = $state<QuarantineRecord[]>([]);
  let stats = $state<QuarantineStats>({ count: 0, bytes: 0 });
  let busy = $state(false);
  let localMessage = $state<string | null>(null);
  let localMessageTimer: number | null = null;

  function flashMessage(text: string): void {
    localMessage = text;
    if (localMessageTimer !== null) {
      window.clearTimeout(localMessageTimer);
    }
    localMessageTimer = window.setTimeout(() => {
      localMessage = null;
      localMessageTimer = null;
    }, 4000);
  }

  async function refresh(): Promise<void> {
    records = await listActiveQuarantine();
    stats = await getQuarantineStats();
  }

  $effect(() => {
    void refresh();
  });

  async function retryOne(id: string): Promise<void> {
    if (busy) return;
    busy = true;
    syncStore.suspend();
    try {
      const op = await requeueFromQuarantine(id);
      if (op) flashMessage(t('quarantine.flashRetriedOne'));
      await refresh();
    } finally {
      syncStore.resume();
      busy = false;
    }
  }

  async function discardOne(id: string): Promise<void> {
    if (busy) return;
    busy = true;
    syncStore.suspend();
    try {
      await markQuarantineDiscarded(id);
      flashMessage(t('quarantine.flashDiscardedOne'));
      await refresh();
    } finally {
      syncStore.resume();
      busy = false;
    }
  }

  async function retryAll(): Promise<void> {
    if (busy) return;
    busy = true;
    syncStore.suspend();
    try {
      const ids = records.map((r) => r.id);
      const ops = await requeueManyFromQuarantine(ids);
      flashMessage(
        t('quarantine.flashRetriedMany', { n: String(ops.length) })
      );
      await refresh();
    } finally {
      syncStore.resume();
      busy = false;
    }
  }

  async function discardAll(): Promise<void> {
    if (busy) return;
    busy = true;
    syncStore.suspend();
    try {
      const ids = records.map((r) => r.id);
      const n = await markManyQuarantineDiscarded(ids);
      flashMessage(t('quarantine.flashDiscardedMany', { n: String(n) }));
      await refresh();
    } finally {
      syncStore.resume();
      busy = false;
    }
  }


  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  function formatDate(iso: string): string {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }
</script>

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('quarantine.title')}</h1>
    {#if records.length > 0}
      <div class="bulk-actions">
        <Button variant="outline" disabled={busy} onclick={retryAll}>
          {t('quarantine.retryAll')}
        </Button>
        <Button variant="danger" disabled={busy} onclick={discardAll}>
          {t('quarantine.discardAll')}
        </Button>
      </div>
    {/if}
  </header>

  <div class="stats">
    <span class="stats-line">
      {stats.count} {t('quarantine.opsLabel')} · {formatBytes(stats.bytes)}
    </span>
  </div>

  {#if localMessage}
    <div class="local-flash" role="status">
      {localMessage}
    </div>
  {/if}

  {#if records.length === 0}
    <EmptyState
      icon="shield-alert"
      title={t('quarantine.empty')}
      description={t('quarantine.emptyHint')}
    />
  {:else}
    <ul class="list">
      {#each records as rec (rec.id)}
        <li class="row">
          <div class="row-info">
            <div class="row-main">
              <span class="entity">{rec.entityType}</span>
              <span class="entity-id">{rec.entityId.slice(0, 8)}…</span>
            </div>
            <div class="row-meta">
              <span class="reason">{rec.reason}</span>
              <span class="dot">·</span>
              <span class="bytes">{formatBytes(rec.bytes ?? 0)}</span>
              <span class="dot">·</span>
              <span class="date">{formatDate(rec.createdAt)}</span>
            </div>
          </div>
          <div class="row-actions">
            <Button
              variant="outline"
              size="sm"
              disabled={busy || rec.payload === null}
              onclick={() => retryOne(rec.id)}
            >
              {t('quarantine.retry')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={busy}
              onclick={() => discardOne(rec.id)}
            >
              {t('quarantine.discard')}
            </Button>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    max-width: 900px;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .bulk-actions {
    display: flex;
    gap: var(--space-2);
  }

  .stats {
    padding: var(--space-3) var(--space-4);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .stats-line {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: var(--text-mono-data);
    color: var(--color-text-secondary);
  }

  .local-flash {
    padding: var(--space-3) var(--space-4);
    background: var(--color-success-tint);
    border-left: 3px solid var(--color-success);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-size: var(--text-sm);
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    min-height: 64px;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .row-info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
    flex: 1;
  }

  .row-main {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .entity {
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
  }

  .entity-id {
    font-family: var(--font-mono);
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }

  .row-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-caption);
    color: var(--color-text-muted);
    flex-wrap: wrap;
  }

  .dot {
    opacity: 0.5;
  }

  .reason {
    color: var(--color-warning);
  }

  .row-actions {
    display: flex;
    gap: var(--space-2);
    flex: 0 0 auto;
  }
</style>